import { useTranslation } from 'react-i18next'
import { Alpha3Code } from 'i18n-iso-countries'

import { SearchAndFilters } from '../searchAndFilters/SearchAndFilters'
import { useCandidates } from '../candidatesContext/useCandidatesContext'
import { formatDate, getCurrentStateBadge } from '../../utils/utils'
import styles from '../CandidatesContainer.module.scss'

import { getCountryNameFromAlpha3Code } from '@percent/partner-dashboard/i18n/countries'
import {
  Badge,
  FlagAvatar,
  Flex,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip
} from '@percent/lemonade'
import { GetCandidatesSortBy } from '@percent/partner-dashboard/services/grants/grantsService.types'
import { AllCandidatesTableProps } from './AllCandidatesTable.types'

const SORT_BY_COLUMN_ID: Record<string, GetCandidatesSortBy> = { organization: 'organisationName' }
const COLUMN_ID_BY_SORT_BY: Record<string, string> = { organisationName: 'organization' }

export function AllCandidatesTable({ ...props }: Readonly<AllCandidatesTableProps>) {
  const { t } = useTranslation()

  const { candidates, sort, navigateToCandidate, queryParams } = useCandidates()

  const isSearchActive = !!queryParams.searchTerm && queryParams.searchTerm.trim() !== ''
  const sortedColumnId = queryParams.sortBy
    ? COLUMN_ID_BY_SORT_BY[queryParams.sortBy] ?? queryParams.sortBy
    : 'submittedAt'

  const columnsHeaders: TableColumn[] = [
    { id: 'country', content: t('table.header.country'), isSortable: false, props: { width: '10%' } },
    { id: 'organization', content: t('table.header.organization'), isSortable: true, props: { width: '60%' } },
    { id: 'stage', content: t('table.header.stage'), isSortable: false, props: { width: '15%' } },
    { id: 'submittedAt', content: t('table.header.submittedAt'), isSortable: true, props: { width: '15%' } }
  ]

  if (candidates.isLoading) {
    return <Loader />
  }

  const emptyTableTitle = isSearchActive
    ? t('grants.searchEmptyTable.title')
    : t('grants.candidatesTable.emptyTable.title')
  const emptyTableDescription = isSearchActive ? '' : t('grants.candidatesTable.emptyTable.description')
  const illustration = isSearchActive ? 'no-results' : 'no-grant-candidates'

  return (
    <TableProvider
      columns={columnsHeaders}
      totalResults={candidates.totalResults}
      isLoading={candidates.isLoading}
      handleRequestSort={columnKey => sort.handleRequestSort(SORT_BY_COLUMN_ID[columnKey] ?? columnKey)}
      sortDirection={sort.direction}
      sortByColumn={sortedColumnId}
      illustration={illustration}
      emptyTableTitle={emptyTableTitle}
      emptyTableDescription={emptyTableDescription}
      className={styles.tableWrapper}
    >
      <Flex direction="column">
        <SearchAndFilters
          validStates={props.enabledFilters.currentState ? props.validStates : []}
          enabledFilters={props.enabledFilters}
        />
      </Flex>
      <Table>
        <TableBody>
          {candidates.data?.map(
            ({ id, organisation, currentState, createdAt, rejectionReason, rejectionReasonDetail }) => {
              const stateBadge = getCurrentStateBadge({
                currentState,
                rejectionReason: rejectionReason ?? undefined,
                rejectionReasonDetail,
                t
              })

              return (
                <TableRow
                  key={`candidate-${id}`}
                  data-testid={`candidate-${id}`}
                  onClick={() => navigateToCandidate(id)}
                  className={styles.tableRow}
                >
                  <TableCell inlineStyle={{ width: '150px' }}>
                    <Flex align="center">
                      <Tooltip
                        content={getCountryNameFromAlpha3Code(organisation.countryCode as Alpha3Code)}
                        placement="right"
                      >
                        <div className={styles.tooltipContent}>
                          <FlagAvatar code3={organisation.countryCode as Alpha3Code} width="28px" />
                        </div>
                      </Tooltip>
                    </Flex>
                  </TableCell>
                  <TableCell>{organisation.name}</TableCell>
                  <TableCell>
                    <Tooltip content={stateBadge.tooltip}>
                      <div>
                        <Badge variant={stateBadge.variant} icon={stateBadge.icon}>
                          {stateBadge.label}
                        </Badge>
                      </div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatDate(createdAt)}</TableCell>
                </TableRow>
              )
            }
          )}
        </TableBody>
      </Table>
      <Flex direction="column" p="10px">
        <TablePagination
          totalResultsText={t('table.results', { count: candidates.totalResults ?? undefined })}
          previousPage={candidates.previousPage}
          nextPage={candidates.nextPage}
        />
      </Flex>
    </TableProvider>
  )
}
